import React, { useEffect, useState } from "react";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderForm } from "../../../interfaces/orderForm";
import {
  getOrderFormByID,
  saveCreditReportAuthorizationForm,
} from "../../../services/orderFormService";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import { SimpleClient } from "../../../interfaces/client";
import { getSimpleClient } from "../../../services/clientService";
import {
  getSafePickerDate,
  getStates,
  isValidBirthday,
  isValidDate,
  isValidSSN,
  isValidZip,
  toSafeDateString,
} from "../../../services/helpers";
import MonthDayYearPicker from "../../../components/component-tools/MonthDayYearPicker";

export interface CreditRelease {
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  dateOfBirth: Date;
  socialSecurityNumber: string;
}

const CreditReportAuthorizationForm = () => {
  const navigate = useNavigate();
  const states = getStates();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const location = useLocation();
  const { orderID, orderFormID, anyCompletedForms } = location.state || {};
  // const [fileUploaded, setFileUploaded] = useState(false);
  // const [file, setFile] = useState<File | null>(null);
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [creditRelease, setCreditRelease] = useState<CreditRelease>({
    firstName: "",
    middleName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    dateOfBirth: new Date(),
    socialSecurityNumber: "",
  });

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);
        const simpleClient = await getSimpleClient(order?.clientID);
        setClient(simpleClient);

        if (anyCompletedForms) {
          setCreditRelease((prev) => ({
            ...prev,
            firstName: order.candidateFirstName ?? "",
            middleName: order.candidateMI ?? "",
            lastName: order.candidateLastName ?? "",
            dateOfBirth: order.candidateDOB ?? new Date(),
            socialSecurityNumber: order.candidateSSN ?? "",
            address: order.candidateAddress ?? "",
            city: order.candidateCity ?? "",
            state: order.candidateState ?? "",
            zipCode: order.candidateZipCode ?? "",
          }));
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderID, orderFormID, anyCompletedForms]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (e.target.type === "date" && field === "dateOfBirth") {
      value = new Date(e.target.value);
    }

    if (field in creditRelease) {
      setCreditRelease((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (field in authorization) {
      setAuthorization((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Credit Release Validation
    if (!creditRelease?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!creditRelease?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!creditRelease?.address) {
      errors.address = "Address is required.";
    }
    if (!creditRelease?.city) {
      errors.city = "City is required.";
    }
    if (!creditRelease?.state) {
      errors.state = "State is required.";
    }
    if (!creditRelease?.zipCode) {
      errors.zipCode = "Zip Code is required.";
    } else if (!isValidZip(creditRelease.zipCode)) {
      errors.zipCode = "Please enter a valid zip code.";
    }
    if (!creditRelease?.socialSecurityNumber) {
      errors.socialSecurityNumber = "Social Security Number is required.";
    } else if (!isValidSSN(creditRelease?.socialSecurityNumber)) {
      errors.socialSecurityNumber = "Enter a valid SSN";
    }
    if (!isValidDate(creditRelease?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (!isValidBirthday(creditRelease.dateOfBirth)) {
      errors.dateOfBirth = "Please check your birth date";
    }

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    // Release Form Validation
    // if (!fileUploaded && creditRelease.state === "VT") {
    //   errors.specialRelease = "Please upload the release form.";
    // }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // const handleFormUpload = async () => {
  //   if (!orderFormID) {
  //     console.error("Order Form ID is missing");
  //     return;
  //   }

  //   try {
  //     setLoadingSpinner(true);
  //     const fileInput = document.createElement("input");
  //     fileInput.type = "file";
  //     fileInput.accept = "image/*,.pdf";
  //     fileInput.onchange = async (event: any) => {
  //       const file = event.target.files[0];
  //       if (!file) return;
  //       setFile(file);
  //       setFileUploaded(true);
  //       setSuccessMessage("Special Release form uploaded successfully");
  //       validationErrors.specialRelease = "";
  //     };
  //     fileInput.click();
  //   } catch (error) {
  //     setFileUploaded(false);
  //     console.error("Error handling file upload:", error);
  //   } finally {
  //     setLoadingSpinner(false);
  //   }
  // };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if ((await validateFields()) && order && orderForm && client) {
        const result = await saveCreditReportAuthorizationForm(
          order,
          orderForm?.orderFormID,
          creditRelease,
          authorization,
          client.primaryFirstName ? client.primaryFirstName : "",
          client.primaryLastName ? client.primaryLastName : "",
          client.primaryTitle ? client.primaryTitle : "",
          client.diocese ? client.diocese : "",
          client.abbreviation ? client.abbreviation : "",
          client.clientName,
        );

        // if (file && fileUploaded) {
        //   await uploadSpecialReleaseForm(file, orderFormID);
        // }

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      }
    } catch (error) {
      console.error("Error saving credit report authorization:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">
            AUTHORIZATION TO RELEASE CREDIT RECORDS
          </div>

          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I understand that the <b>{client?.diocese}</b> also includes a
              credit check as part of its background investigation on candidates
              for certain positions. Should a credit check reveal a significant
              problem in this area, the information will be given directly and
              confidentially to{" "}
              <b>
                {client?.primaryTitle} {client?.primaryFirstName}{" "}
                {client?.primaryLastName}{" "}
              </b>
              or their designated agents.{" "}
            </div>

            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I authorize this check to be completed, and hereby release and
              hold harmless from liability any person or organization that
              provides such information.
            </div>

            <div className="text-body" style={{ flex: "0 0 100%" }}>
              *To find more information regarding your rights under the Fair
              Credit Reporting Act (FCRA), please visit the{" "}
              <a
                href="https://www.consumerfinance.gov/learnmore/."
                target="_blank"
                rel="noopener noreferrer"
              >
                CFPB
              </a>{" "}
              website. If you are a current <b>Vermont resident</b>, please see
              the attached{" "}
              <a
                href="https://oxfordstoreacctdev.blob.core.windows.net/documents/VT.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                statute
              </a>
              .
            </div>

            {/* <div className="text-container" style={{ flex: "0 0 30%" }}>
              If you live in the state of <b>Vermont </b>please fill out this
              form.
            </div>
            <div style={{ flex: "0 0 50%" }}>
              {fileUploaded ? (
                <button className="oxbtn btn-grey">
                  <i className="bi bi-check2-circle icon-large"></i>
                </button>
              ) : (
                <button
                  className="oxbtn btn-grey"
                  onClick={() => handleFormUpload()}
                >
                  Upload Form
                </button>
              )}
              {validationErrors.specialRelease && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.specialRelease}
                </div>
              )}
            </div> */}

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">First Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.firstName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={creditRelease?.firstName || ""}
                  onChange={(e) => handleInputChange(e, "firstName")}
                />
                {validationErrors.firstName && (
                  <div className="invalid-feedback">
                    {validationErrors.firstName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Middle Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={creditRelease?.middleName || ""}
                  onChange={(e) => handleInputChange(e, "middleName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Last Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.lastName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={creditRelease?.lastName || ""}
                  onChange={(e) => handleInputChange(e, "lastName")}
                />
                {validationErrors.lastName && (
                  <div className="invalid-feedback">
                    {validationErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Address:</label>
              <input
                className={`form-control ${
                  validationErrors.address ? "is-invalid" : ""
                }`}
                type="text"
                value={creditRelease.address || ""}
                onChange={(e) => handleInputChange(e, "address")}
              />
              {validationErrors.address && (
                <div className="invalid-feedback">
                  {validationErrors.address}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">City:</label>
              <input
                className={`form-control ${
                  validationErrors.city ? "is-invalid" : ""
                }`}
                type="text"
                value={creditRelease.city}
                onChange={(e) => handleInputChange(e, "city")}
              />
              {validationErrors.city && (
                <div className="invalid-feedback">{validationErrors.city}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">State:</label>
              <select
                className={`form-select ${
                  validationErrors.state ? "is-invalid" : ""
                }`}
                value={creditRelease.state}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
              {validationErrors.state && (
                <div className="invalid-feedback">{validationErrors.state}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Zip Code:</label>
              <input
                className={`form-control ${
                  validationErrors.zipCode ? "is-invalid" : ""
                }`}
                type="text"
                value={creditRelease.zipCode}
                onChange={(e) => handleInputChange(e, "zipCode")}
              />
              {validationErrors.zipCode && (
                <div className="invalid-feedback">
                  {validationErrors.zipCode}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Date of Birth:</label>
              <MonthDayYearPicker
                value={getSafePickerDate(creditRelease.dateOfBirth)}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: toSafeDateString(newDate) } } as any,
                    "dateOfBirth",
                  )
                }
              />
              {validationErrors.dateOfBirth && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.dateOfBirth}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Social Security Number:</label>
              <input
                className={`form-control ${
                  validationErrors.socialSecurityNumber ? "is-invalid" : ""
                }`}
                type="text"
                value={creditRelease.socialSecurityNumber}
                onChange={(e) => handleInputChange(e, "socialSecurityNumber")}
              />
              {validationErrors.socialSecurityNumber && (
                <div className="invalid-feedback">
                  {validationErrors.socialSecurityNumber}
                </div>
              )}
            </div>
          </div>
          <AuthorizationForm
            authText={
              "I attest that the answers I have given are true to the best of my knowledge and hereby consent to the release of the above-described records."
            }
            authorization={authorization}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            type={""}
          />

          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default CreditReportAuthorizationForm;
